// ProcessorMonitoringDashboard.js - Admin dashboard for processor monitoring
import React, { useState, useEffect } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  XCircle,
  Zap,
  TrendingUp,
  Server,
  AlertCircle,
  Eye,
} from "lucide-react";
import api from "./api";
import "./ProcessorMonitoringDashboard.css";

const ProcessorMonitoringDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [monitoringStats, setMonitoringStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revalidating, setRevalidating] = useState(false);
  const [selectedProcessor, setSelectedProcessor] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchMonitoringStats();

    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchMonitoringStats();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get("/processors/admin/monitoring/dashboard");
      setDashboardData(response.data);
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
      setError("Failed to load dashboard data");
    }
  };

  const fetchMonitoringStats = async () => {
    try {
      const response = await api.get("/processors/admin/monitoring/stats");
      setMonitoringStats(response.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch monitoring stats:", err);
      setError("Failed to load monitoring statistics");
      setLoading(false);
    }
  };

  const handleRevalidateAll = async () => {
    try {
      setRevalidating(true);
      await api.post("/processors/admin/monitoring/revalidate-all");
      setTimeout(() => {
        fetchDashboardData();
        fetchMonitoringStats();
      }, 2000); // Delay to allow jobs to queue
    } catch (err) {
      setError("Failed to trigger revalidation");
    } finally {
      setRevalidating(false);
    }
  };

  const handleRevalidateProcessor = async (processorId) => {
    try {
      await api.post(`/processors/admin/${processorId}/monitoring/revalidate`);
      setTimeout(() => {
        fetchDashboardData();
        fetchMonitoringStats();
      }, 1000);
    } catch (err) {
      setError(`Failed to revalidate processor ${processorId}`);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "valid":
        return <CheckCircle size={16} className="text-green-500" />;
      case "invalid":
        return <XCircle size={16} className="text-red-500" />;
      case "error":
        return <AlertTriangle size={16} className="text-orange-500" />;
      case "pending":
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "valid":
        return "bg-green-100 text-green-800";
      case "invalid":
        return "bg-red-100 text-red-800";
      case "error":
        return "bg-orange-100 text-orange-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return "Never";
    const now = Date.now();
    const date = new Date(dateString).getTime();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (loading) {
    return <div className="loading">Loading monitoring dashboard...</div>;
  }

  return (
    <div className="monitoring-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>
            <Activity size={24} />
            Processor Monitoring
          </h2>
          <div className="header-actions">
            <button
              onClick={() => {
                fetchDashboardData();
                fetchMonitoringStats();
              }}
              className="btn-secondary"
              title="Refresh data"
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button
              onClick={handleRevalidateAll}
              className="btn-primary"
              disabled={revalidating}
            >
              {revalidating ? (
                <>
                  <RefreshCw size={16} className="spinning" />
                  Revalidating...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  Revalidate All
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="error-alert">
            <AlertCircle size={20} />
            {error}
          </div>
        )}
      </div>

      {/* Statistics Cards */}
      {monitoringStats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Total Processors</div>
              <div className="stat-value">
                {monitoringStats.processors.total}
              </div>
              <div className="stat-detail">
                {monitoringStats.processors.autoDiscovered} auto-discovered
              </div>
            </div>
            <Server className="stat-icon" />
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Healthy</div>
              <div className="stat-value text-green-600">
                {monitoringStats.monitoring.healthyProcessors}
              </div>
              <div className="stat-detail">
                {monitoringStats.processors.validationStatus?.valid || 0}{" "}
                validated
              </div>
            </div>
            <CheckCircle className="stat-icon text-green-500" />
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Need Attention</div>
              <div className="stat-value text-orange-600">
                {monitoringStats.monitoring.needingRevalidation}
              </div>
              <div className="stat-detail">Requiring revalidation</div>
            </div>
            <AlertTriangle className="stat-icon text-orange-500" />
          </div>

          <div className="stat-card">
            <div className="stat-content">
              <div className="stat-label">Queue Status</div>
              <div className="stat-value">{monitoringStats.queue.active}</div>
              <div className="stat-detail">
                {monitoringStats.queue.waiting} waiting
              </div>
            </div>
            <TrendingUp className="stat-icon" />
          </div>
        </div>
      )}

      {dashboardData && (
        <>
          {/* Processors Needing Attention */}
          {dashboardData.needingAttention.length > 0 && (
            <div className="section">
              <h3 className="section-title">
                <AlertTriangle size={20} />
                Processors Needing Attention (
                {dashboardData.needingAttention.length})
              </h3>
              <div className="processor-grid">
                {dashboardData.needingAttention.map((processor) => (
                  <div key={processor.id} className="processor-card attention">
                    <div className="processor-header">
                      <div className="processor-title">
                        <span className="processor-name">{processor.name}</span>
                        <div className="processor-badges">
                          {getStatusIcon(processor.validationStatus)}
                          <span
                            className={`status-badge ${getStatusColor(
                              processor.validationStatus
                            )}`}
                          >
                            {processor.validationStatus}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRevalidateProcessor(processor.id)}
                        className="btn-small"
                        title="Revalidate processor"
                      >
                        <RefreshCw size={14} />
                      </button>
                    </div>

                    <div className="processor-details">
                      <div className="detail-item">
                        <span className="detail-label">Last Validated:</span>
                        <span className="detail-value">
                          {getTimeSince(processor.lastValidatedAt)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Discovery URL:</span>
                        <span className="detail-value truncate">
                          {processor.discoveryUrl}
                        </span>
                      </div>
                      {processor.validationDetails?.errors?.length > 0 && (
                        <div className="validation-errors">
                          <strong>Errors:</strong>
                          <ul>
                            {processor.validationDetails.errors
                              .slice(0, 2)
                              .map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Processors */}
          <div className="section">
            <h3 className="section-title">
              <Server size={20} />
              All Auto-Discovered Processors ({dashboardData.processors.length})
            </h3>

            <div className="processor-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Last Validated</th>
                    <th>Discovery URL</th>
                    <th>Published</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboardData.processors.map((processor) => (
                    <tr
                      key={processor.id}
                      className={
                        processor.needsRevalidation ? "needs-attention" : ""
                      }
                    >
                      <td>
                        <div className="processor-name-cell">
                          <span className="name">{processor.name}</span>
                          <span className="id">{processor.id}</span>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          {getStatusIcon(processor.validationStatus)}
                          <span
                            className={`status-badge ${getStatusColor(
                              processor.validationStatus
                            )}`}
                          >
                            {processor.validationStatus}
                          </span>
                          {processor.needsRevalidation && (
                            <AlertTriangle
                              size={12}
                              className="text-orange-500 ml-1"
                              title="Needs revalidation"
                            />
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="time-cell">
                          <span className="time">
                            {getTimeSince(processor.lastValidatedAt)}
                          </span>
                          <span className="full-time">
                            {formatDateTime(processor.lastValidatedAt)}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="url truncate"
                          title={processor.discoveryUrl}
                        >
                          {processor.discoveryUrl}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`publish-status ${
                            processor.published ? "published" : "draft"
                          }`}
                        >
                          {processor.published ? "Published" : "Draft"}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => setSelectedProcessor(processor)}
                            className="btn-small"
                            title="View details"
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            onClick={() =>
                              handleRevalidateProcessor(processor.id)
                            }
                            className="btn-small"
                            title="Revalidate"
                          >
                            <RefreshCw size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity */}
          {dashboardData.recentActivity.length > 0 && (
            <div className="section">
              <h3 className="section-title">Recent Validation Activity</h3>
              <div className="activity-list">
                {dashboardData.recentActivity.slice(0, 10).map((processor) => (
                  <div key={processor.id} className="activity-item">
                    <div className="activity-icon">
                      {getStatusIcon(processor.validationStatus)}
                    </div>
                    <div className="activity-content">
                      <span className="activity-name">{processor.name}</span>
                      <span className="activity-detail">
                        Validated {getTimeSince(processor.lastValidatedAt)} -{" "}
                        {processor.validationStatus}
                      </span>
                    </div>
                    <div className="activity-time">
                      {formatDateTime(processor.lastValidatedAt)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Processor Details Modal */}
      {selectedProcessor && (
        <div className="modal">
          <div className="modal-content large">
            <div className="modal-header">
              <h2>Processor Details: {selectedProcessor.name}</h2>
              <button
                className="modal-close"
                onClick={() => setSelectedProcessor(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="processor-details-modal">
              <div className="detail-section">
                <h4>General Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>ID:</label>
                    <span>{selectedProcessor.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <div className="status-cell">
                      {getStatusIcon(selectedProcessor.validationStatus)}
                      <span
                        className={`status-badge ${getStatusColor(
                          selectedProcessor.validationStatus
                        )}`}
                      >
                        {selectedProcessor.validationStatus}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <label>Discovery URL:</label>
                    <span className="url">
                      {selectedProcessor.discoveryUrl}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Last Validated:</label>
                    <span>
                      {formatDateTime(selectedProcessor.lastValidatedAt)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Discovered:</label>
                    <span>
                      {formatDateTime(selectedProcessor.discoveredAt)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Published:</label>
                    <span>{selectedProcessor.published ? "Yes" : "No"}</span>
                  </div>
                </div>
              </div>

              {selectedProcessor.validationDetails && (
                <div className="detail-section">
                  <h4>Validation Details</h4>
                  <div className="validation-status">
                    <div className="validation-checks">
                      <div
                        className={`check-item ${
                          selectedProcessor.validationDetails.endpointsReachable
                            ? "pass"
                            : "fail"
                        }`}
                      >
                        {selectedProcessor.validationDetails
                          .endpointsReachable ? (
                          <CheckCircle size={16} />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Endpoints Reachable
                      </div>
                      <div
                        className={`check-item ${
                          selectedProcessor.validationDetails.contractValid
                            ? "pass"
                            : "fail"
                        }`}
                      >
                        {selectedProcessor.validationDetails.contractValid ? (
                          <CheckCircle size={16} />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Contract Valid
                      </div>
                      <div
                        className={`check-item ${
                          selectedProcessor.validationDetails.healthCheckPassed
                            ? "pass"
                            : "fail"
                        }`}
                      >
                        {selectedProcessor.validationDetails
                          .healthCheckPassed ? (
                          <CheckCircle size={16} />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Health Check Passed
                      </div>
                      <div
                        className={`check-item ${
                          selectedProcessor.validationDetails
                            .testProcessingPassed
                            ? "pass"
                            : "fail"
                        }`}
                      >
                        {selectedProcessor.validationDetails
                          .testProcessingPassed ? (
                          <CheckCircle size={16} />
                        ) : (
                          <XCircle size={16} />
                        )}
                        Test Processing Passed
                      </div>
                    </div>

                    {selectedProcessor.validationDetails.errors?.length > 0 && (
                      <div className="errors-section">
                        <h5>Errors:</h5>
                        <ul>
                          {selectedProcessor.validationDetails.errors.map(
                            (error, i) => (
                              <li key={i}>{error}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}

                    {selectedProcessor.validationDetails.warnings?.length >
                      0 && (
                      <div className="warnings-section">
                        <h5>Warnings:</h5>
                        <ul>
                          {selectedProcessor.validationDetails.warnings.map(
                            (warning, i) => (
                              <li key={i}>{warning}</li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessorMonitoringDashboard;
